/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  CheckCircle2,
  Clock,
  HandHelping,
  Pencil,
  Phone,
  Plus,
  QrCode,
  Trash2,
  X,
} from "lucide-react";
import { dailyHelpApi, HELP_KINDS, helpKindLabel } from "./api";
import { toast } from "../ui";
import "./DailyHelp.css";

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return "";
  }
}

export default function DailyHelpPage({ embedded = false }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // staff object being edited
  const [expanded, setExpanded] = useState(null); // staff id → attendance/QR open

  const load = useCallback(async (signal) => {
    try {
      const d = await dailyHelpApi.list({ signal });
      setStaff(d.staff || []);
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Could not load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const c = new AbortController();
    load(c.signal);
    return () => c.abort();
  }, [load]);

  const retire = async (s) => {
    if (!window.confirm(`Remove ${s.name} from your daily help?`)) return;
    try {
      await dailyHelpApi.retire(s.id);
      setStaff((prev) => prev.filter((x) => x.id !== s.id));
      toast.success(`${s.name} removed`);
    } catch (e) {
      toast.error(e.message || "Could not remove");
    }
  };

  return (
    <div className={embedded ? "" : "dh-shell"}>
      <div className="dh-head">
        {!embedded && (
          <div>
            <h1>
              <HandHelping size={22} /> Daily Help
            </h1>
            <p className="dh-sub">
              Register your domestic staff — maid, cook, driver, nanny. The gate
              marks them in/out and you're notified each time they arrive or leave.
            </p>
          </div>
        )}
        <button className="dh-btn-primary" onClick={() => setShowAdd(true)}>
          <Plus size={15} /> Add help
        </button>
      </div>

      {loading ? (
        <div className="dh-empty">Loading…</div>
      ) : staff.length === 0 ? (
        <div className="dh-empty-card">
          <HandHelping size={30} strokeWidth={1.4} />
          <h3>No daily help yet</h3>
          <p>Add your maid, cook or driver so the gate can mark their attendance.</p>
        </div>
      ) : (
        <div className="dh-grid">
          {staff.map((s) => (
            <div key={s.id} className="dh-card">
              <div className="dh-card-top">
                <span className="dh-av">{(s.name || "?").charAt(0).toUpperCase()}</span>
                <div className="dh-card-main">
                  <strong>{s.name}</strong>
                  <span className="dh-meta">
                    {helpKindLabel(s.kind)}
                    {s.phone ? ` · ${s.phone}` : ""}
                  </span>
                </div>
                <span className={`dh-pill ${s.inside ? "in" : "out"}`}>
                  {s.inside ? "Inside" : "Outside"}
                </span>
              </div>

              <div className="dh-card-actions">
                <span className="dh-code">
                  <QrCode size={13} /> {s.short_code}
                </span>
                <button
                  className="dh-link"
                  onClick={() =>
                    setExpanded(expanded === s.id ? null : s.id)
                  }
                >
                  {expanded === s.id ? "Hide" : "QR & attendance"}
                </button>
                {s.phone && (
                  <a className="dh-icon" href={`tel:+91${s.phone}`} title="Call">
                    <Phone size={14} />
                  </a>
                )}
                <button
                  className="dh-icon"
                  onClick={() => setEditTarget(s)}
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  className="dh-icon dh-icon--danger"
                  onClick={() => retire(s)}
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {expanded === s.id && (
                <HelpDetail staff={s} />
              )}
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddHelpModal
          onClose={() => setShowAdd(false)}
          onAdded={(s) => {
            setStaff((prev) => [...prev, s].sort((a, b) => a.name.localeCompare(b.name)));
            setShowAdd(false);
          }}
        />
      )}

      {editTarget && (
        <EditHelpModal
          staff={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(updated) => {
            setStaff((prev) =>
              prev.map((x) => (x.id === updated.id ? updated : x))
            );
            setEditTarget(null);
          }}
        />
      )}
    </div>
  );
}

function HelpDetail({ staff }) {
  const [sessions, setSessions] = useState(null);
  useEffect(() => {
    let alive = true;
    dailyHelpApi
      .attendance(staff.id)
      .then((d) => alive && setSessions(d.sessions || []))
      .catch(() => alive && setSessions([]));
    return () => {
      alive = false;
    };
  }, [staff.id]);

  return (
    <div className="dh-detail">
      <div className="dh-qr">
        {staff.code ? (
          <QRCodeSVG value={staff.code} size={104} level="M" />
        ) : null}
        <span className="dh-qr-note">Show at the gate · code {staff.short_code}</span>
      </div>
      <div className="dh-att">
        <div className="dh-att-h">
          <Clock size={13} /> Recent attendance
        </div>
        {sessions === null ? (
          <div className="dh-muted">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="dh-muted">No visits yet.</div>
        ) : (
          <ul>
            {sessions.slice(0, 6).map((a) => (
              <li key={a.id}>
                <CheckCircle2 size={12} /> In {fmtDateTime(a.entered_at)}
                {a.exited_at ? ` · Out ${fmtDateTime(a.exited_at)}` : " · still inside"}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EditHelpModal({ staff, onClose, onSaved }) {
  const [name, setName] = useState(staff.name || "");
  const [kind, setKind] = useState(staff.kind || "MAID");
  const [phone, setPhone] = useState(staff.phone || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!name.trim()) return setError("Enter a name");
    if (phone && !/^[0-9]{10}$/.test(phone)) return setError("Phone must be 10 digits");
    setBusy(true);
    try {
      const updated = await dailyHelpApi.update(staff.id, {
        name: name.trim(),
        kind,
        phone: phone.trim() || null,
      });
      toast.success("Updated");
      onSaved(updated);
    } catch (e) {
      setError(e.message || "Could not save");
      setBusy(false);
    }
  };

  return (
    <div className="dh-modal-overlay" onClick={onClose}>
      <div className="dh-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dh-modal-head">
          <h2>Edit daily help</h2>
          <button onClick={onClose} className="dh-x">
            <X size={18} />
          </button>
        </div>
        <div className="dh-modal-body">
          <label className="dh-field">
            <span>Name *</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Lakshmi" />
          </label>
          <label className="dh-field">
            <span>Role</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              {HELP_KINDS.map((k) => (
                <option key={k} value={k}>
                  {helpKindLabel(k)}
                </option>
              ))}
            </select>
          </label>
          <label className="dh-field">
            <span>Phone (optional)</span>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              maxLength={10}
              placeholder="10-digit mobile"
            />
          </label>
          {error && <div className="dh-error">{error}</div>}
        </div>
        <div className="dh-modal-foot">
          <button className="dh-btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="dh-btn-primary" onClick={submit} disabled={busy}>
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddHelpModal({ onClose, onAdded }) {
  const [villas, setVillas] = useState([]);
  const [villaId, setVillaId] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState("MAID");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    dailyHelpApi
      .myVillas()
      .then((vs) => {
        setVillas(vs);
        if (vs.length === 1) setVillaId(vs[0].id);
      })
      .catch(() => {});
  }, []);

  const submit = async () => {
    setError("");
    if (!name.trim()) return setError("Enter a name");
    if (!villaId) return setError("Pick a villa");
    if (phone && !/^[0-9]{10}$/.test(phone)) return setError("Phone must be 10 digits");
    setBusy(true);
    try {
      const s = await dailyHelpApi.create({
        villa_id: villaId,
        name: name.trim(),
        kind,
        phone: phone.trim() || undefined,
      });
      toast.success(`${s.name} added`);
      onAdded(s);
    } catch (e) {
      setError(e.message || "Could not add");
      setBusy(false);
    }
  };

  return (
    <div className="dh-modal-overlay" onClick={onClose}>
      <div className="dh-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dh-modal-head">
          <h2>Add daily help</h2>
          <button onClick={onClose} className="dh-x">
            <X size={18} />
          </button>
        </div>
        <div className="dh-modal-body">
          <label className="dh-field">
            <span>Name *</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Lakshmi" />
          </label>
          <label className="dh-field">
            <span>Role</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              {HELP_KINDS.map((k) => (
                <option key={k} value={k}>
                  {helpKindLabel(k)}
                </option>
              ))}
            </select>
          </label>
          <label className="dh-field">
            <span>Phone (optional)</span>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              maxLength={10}
              placeholder="10-digit mobile"
            />
          </label>
          {villas.length > 1 && (
            <label className="dh-field">
              <span>Villa *</span>
              <select value={villaId} onChange={(e) => setVillaId(e.target.value)}>
                <option value="">Select…</option>
                {villas.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.number}
                  </option>
                ))}
              </select>
            </label>
          )}
          {error && <div className="dh-error">{error}</div>}
        </div>
        <div className="dh-modal-foot">
          <button className="dh-btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="dh-btn-primary" onClick={submit} disabled={busy}>
            {busy ? "Adding…" : "Add help"}
          </button>
        </div>
      </div>
    </div>
  );
}
